/* =====================================================================
   PRODUCT PAGE REDESIGN — controller
   Design doc:  docs/superpowers/specs/2026-08-18-pdp-redesign-ab-test-design.md
   Styles:      assets/pdp-redesign.css   (all rules scoped to html.pdp-redesign)
   Loader:      snippets/product-overrides.liquid (stylesheet, <head>)
                layout/theme.liquid (this file, deferred, after theme.js)

   This started life as the `product-page-redesign` PostHog A/B test. It won,
   so on 2026-08-19 it graduated to permanent: the `pdp-redesign` class is
   written onto <html> server-side in layout/theme.liquid and there is no flag
   read, no exposure event and no localStorage pre-paint hint left here.

   What it does
   ------------
   Applies a set of idempotent DOM enhancements on top of the theme's own
   product markup:
     - brand row (brand text + "Shop all" side by side)
     - size grid (theme's own buildSizeGrid via window.NSL) + UK/EU toggle
       + size header / approximate line + "crossed out" legend
     - width chips get a fitting label under the letter
     - quantity stepper moved beside the width chips
     - store availability moved into the delivery meta row under the CTAs
     - Tanya's take under the thumbnails on desktop
     - Delivery & Returns accordion (from the <template> in main-product)
     - gallery dots (mobile) + thumbnails re-measure
     - Add to Basket label kept in sync with availability
     - empty star rating hidden when a product has no reviews
     - Swym wishlist button kept next to the ATC
     - fixed bottom bar on mobile (price, selection, ATC proxy, heart)
   Everything else is CSS.

   Two PostHog events are still captured as ordinary product analytics (they
   were the test's secondary metrics and remain useful):
   `pdp_redesign_atc_click` (source: main|sticky) and `pdp_redesign_size_unit`.

   QA
   --
   ?ab_debug (or localhost) logs as [pdp-redesign]. `window.__applyPdpRedesign()`
   re-runs the enhancements by hand.

   Reverting
   ---------
   To take the redesign off the site: remove the `pdp-redesign` class from the
   <html> tag in layout/theme.liquid. That alone restores the old product page,
   since every rule here and in the stylesheet is scoped to it. To remove it
   entirely, also delete this file, assets/pdp-redesign.css, the
   {% render 'product-overrides' %} stylesheet line, the
   <template data-pdp-r-delivery-template> + the two `redesign_*` settings in
   sections/main-product.liquid, and the window.NSL block at the end of the
   size-conversion IIFE in assets/theme.js (harmless if left).
   ===================================================================== */
(function () {
  'use strict';

  var CLASS = 'pdp-redesign';
  var UNIT_KEY = 'nsl_size_unit';
  var DEBUG = (window.location.search.indexOf('ab_debug') !== -1) ||
    (window.location.hostname === 'localhost') ||
    (window.location.hostname.indexOf('127.0.0.1') === 0);

  var root = document.documentElement;
  var MQ_DESKTOP = window.matchMedia('(min-width: 981px)');   /* two-column grid */
  var MQ_STACKED = window.matchMedia('(max-width: 768px)');   /* theme's mobile reorder */

  var state = {
    active: true,
    enhanced: false,
    section: null,
    unit: 'uk',
    formConfig: null,
    sticky: null,
    swymObserver: null
  };

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[pdp-redesign]');
    try { console.log.apply(console, args); } catch (e) {}
  }

  function store(key, value) { try { window.localStorage.setItem(key, value); } catch (e) {} }
  function read(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }

  function el(tag, className, html) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
  }

  function insertAfter(node, ref) {
    if (!ref || !ref.parentNode) return;
    ref.parentNode.insertBefore(node, ref.nextSibling);
  }

  function capture(name, props) {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(name, props || {});
      }
    } catch (e) {}
  }


  /* ------------------------------------------------------------------ */
  /* Enhancements                                                        */
  /* ------------------------------------------------------------------ */

  function enhance() {
    if (!state.section) state.section = document.querySelector('.product__section');
    if (!state.section) { log('no .product__section on this page'); return; }
    if (!state.enhanced) {
      state.enhanced = true;
      log('enhancing');
      state.formConfig = readFormConfig();
      safe(buildBrandRow, 'brand row');
      safe(tidyOptionNames, 'option names');
      safe(buildSizeGrid, 'size grid');
      safe(buildWidthLabels, 'width labels');
      safe(moveQuantity, 'quantity');
      safe(moveStoreAvailability, 'store availability');
      safe(hideEmptyDescription, 'empty description');
      safe(hideEmptyRating, 'empty rating');
      safe(insertDeliveryAccordion, 'delivery accordion');
      safe(buildGalleryDots, 'gallery dots');
      safe(syncAtcLabel, 'atc label');
      safe(watchSwym, 'swym');
      safe(buildStickyBar, 'sticky bar');
      safe(bindUpdates, 'updates');
      onMediaChange(MQ_DESKTOP, layout);
      onMediaChange(MQ_STACKED, layout);
      /* Flickity measured the thumbnail strip while it was hidden on mobile
         and the gallery column at its control width; ask it to re-measure
         its control width. */
      setTimeout(function () { window.dispatchEvent(new Event('resize')); }, 80);
    }
    safe(layout, 'layout');
  }

  function safe(fn, label) {
    try { fn(); } catch (e) { log('enhancement failed: ' + label, e); }
  }

  function onMediaChange(mq, fn) {
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', fn);
    else if (typeof mq.addListener === 'function') mq.addListener(fn);
  }

  function readFormConfig() {
    var form = state.section.querySelector('[data-product-form]');
    var cfg = null;
    if (form) { try { cfg = JSON.parse(form.getAttribute('data-product-form') || '{}'); } catch (e) {} }
    cfg = cfg || {};
    return {
      button: cfg.button || 'Add to Basket',
      soldOut: cfg.sold_out || 'Sold Out'
    };
  }

  function gender() {
    var c = state.section.querySelector('[data-product-gender]');
    return c ? (c.getAttribute('data-product-gender') || 'womens') : 'womens';
  }

  /* --- Brand row: brand text + "Shop all" link on one line ------------ */
  function buildBrandRow() {
    if (state.section.querySelector('.pdp-r-brand-row')) return;
    var brand = state.section.querySelector('.brand-logo--product');
    var shopAll = state.section.querySelector('.product__shop-all-brand');
    if (!brand && !shopAll) return;
    var row = el('div', 'pdp-r-brand-row');
    var anchor = brand || shopAll;
    anchor.parentNode.insertBefore(row, anchor);
    if (brand) row.appendChild(brand);
    if (shopAll) row.appendChild(shopAll);
  }

  /* --- "Size:" -> "Size" and friends ---------------------------------- */
  function tidyOptionNames() {
    state.section.querySelectorAll('.swatches__option-name > strong').forEach(function (strong) {
      strong.textContent = strong.textContent.replace(/\s*:\s*$/, '');
    });
  }

  /* --- Size grid + UK/EU toggle -------------------------------------- */
  function sizeContainer() {
    return state.section.querySelector('.swatches__container[data-size-option]');
  }

  function buildSizeGrid() {
    var c = sizeContainer();
    if (!c || !window.NSL || typeof window.NSL.buildSizeGrid !== 'function') return;

    var saved = read(UNIT_KEY);
    state.unit = (saved === 'eu') ? 'eu' : 'uk';
    window.NSL.setSizeUnit(state.unit);
    window.NSL.buildSizeGrid();

    var head = c.querySelector('.swatches__option-name');
    if (head && !head.querySelector('.pdp-r-size-selected')) {
      var selected = el('span', 'pdp-r-size-selected');
      var rawValue = head.querySelector('.swatches__option-value');
      if (rawValue) insertAfter(selected, rawValue); else head.appendChild(selected);
    }
    if (head && !head.querySelector('.pdp-r-unit')) {
      var toggle = el('div', 'pdp-r-unit', '');
      toggle.setAttribute('role', 'group');
      toggle.setAttribute('aria-label', 'Size system');
      ['uk', 'eu'].forEach(function (u) {
        var b = el('button', 'pdp-r-unit__btn', u.toUpperCase());
        b.type = 'button';
        b.setAttribute('data-unit', u);
        b.addEventListener('click', function () { setUnit(u); });
        toggle.appendChild(b);
      });
      var guide = head.querySelector('.popup_content_link');
      if (guide) head.insertBefore(toggle, guide); else head.appendChild(toggle);
    }
    if (!c.querySelector('.pdp-r-size-legend')) {
      var legend = el('p', 'pdp-r-size-legend',
        '<span class="pdp-r-size-legend__swatch" aria-hidden="true"></span>Crossed out = out of stock');
      c.appendChild(legend);
    }
    renderSizeHeader();
  }

  function setUnit(unit) {
    if (unit !== 'uk' && unit !== 'eu') return;
    if (unit === state.unit) return;
    state.unit = unit;
    store(UNIT_KEY, unit);
    if (window.NSL && window.NSL.setSizeUnit) window.NSL.setSizeUnit(unit);
    renderSizeHeader();
    updateSticky();
    capture('pdp_redesign_size_unit', { unit: unit });
  }

  /* Header "Size  UK J10½" + "Approximately: EU 28½", honouring the unit.
     The theme writes its own hint 50ms after a change; we run after it. */
  function renderSizeHeader() {
    var c = sizeContainer();
    if (!c) return;
    var head = c.querySelector('.swatches__option-name');
    var raw = c.querySelector('.swatches__option-value');
    var out = head && head.querySelector('.pdp-r-size-selected');
    var approx = c.querySelector('.size-conversion-hint');
    if (head) {
      head.querySelectorAll('.pdp-r-unit__btn').forEach(function (b) {
        var on = b.getAttribute('data-unit') === state.unit;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
    if (!out) return;
    var value = raw ? raw.textContent.trim() : '';
    var conv = (window.NSL && window.NSL.convertSize) ? window.NSL.convertSize(value, gender()) : null;
    if (!conv) { out.textContent = value; return; }
    var euFirst = state.unit === 'eu';
    var primary = euFirst ? (conv.eu ? 'EU ' + conv.eu : (conv.uk ? 'UK ' + conv.uk : value))
                          : (conv.uk ? 'UK ' + conv.uk : (conv.eu ? 'EU ' + conv.eu : value));
    var secondary = euFirst ? (conv.uk ? 'UK ' + conv.uk : '') : (conv.eu ? 'EU ' + conv.eu : '');
    out.textContent = primary;
    if (approx) approx.innerHTML = secondary ? 'Approximately: <strong>' + secondary + '</strong>' : '';
  }

  /* Formatted selected size for the sticky bar ("UK J10½"). */
  function selectedSizeText() {
    var c = sizeContainer();
    if (!c) return '';
    var raw = c.querySelector('.swatches__option-value');
    var value = raw ? raw.textContent.trim() : '';
    var conv = (window.NSL && window.NSL.convertSize) ? window.NSL.convertSize(value, gender()) : null;
    if (!conv) return value;
    if (state.unit === 'eu') return conv.eu ? 'EU ' + conv.eu : (conv.uk ? 'UK ' + conv.uk : value);
    return conv.uk ? 'UK ' + conv.uk : (conv.eu ? 'EU ' + conv.eu : value);
  }

  /* --- Width chips: letter + fitting label ---------------------------- */
  var WIDTH_LABELS = {
    kids:   { E: 'Narrow', F: 'Standard', G: 'Wide', H: 'Extra wide' },
    womens: { C: 'Narrow', D: 'Standard', E: 'Wide', EE: 'Extra wide' },
    mens:   { F: 'Narrow', G: 'Standard', H: 'Wide', HH: 'Extra wide' }
  };

  function widthContainer() {
    var containers = state.section.querySelectorAll('.swatches__container');
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].hasAttribute('data-size-option')) continue;
      var strong = containers[i].querySelector('.swatches__option-name > strong');
      var name = strong ? strong.textContent.trim().toLowerCase() : '';
      if (/width|ancho|breite|largeur|fitting/.test(name)) return containers[i];
    }
    return null;
  }

  function widthLabelFor(value) {
    var key = (value || '').toUpperCase().replace(/\s|FITTING|FIT|WIDTH/g, '');
    if (!/^[A-Z]{1,2}$/.test(key)) return '';
    var map = WIDTH_LABELS[gender()] || WIDTH_LABELS.womens;
    return map[key] || '';
  }

  function buildWidthLabels() {
    var c = widthContainer();
    if (!c) return;
    c.classList.add('pdp-r-width');
    c.querySelectorAll('.swatch-element').forEach(function (sw) {
      var label = sw.querySelector('.swatches__form--label');
      var input = sw.querySelector('input[type="radio"]');
      if (!label || label.querySelector('.pdp-r-main')) return;
      var value = (input && input.value) || label.textContent.trim();
      var hidden = label.querySelector('.label-unavailable');
      var main = el('span', 'pdp-r-main', '');
      main.textContent = value.trim();
      var sub = el('span', 'pdp-r-sub', '');
      sub.textContent = widthLabelFor(value);
      label.textContent = '';
      label.appendChild(main);
      if (sub.textContent) label.appendChild(sub);
      if (hidden) label.appendChild(hidden);
      sw.classList.add('pdp-r-width__chip');
    });
    var head = c.querySelector('.swatches__option-name');
    if (head && !head.querySelector('.pdp-r-width-label')) {
      var span = el('span', 'pdp-r-width-label');
      var raw = head.querySelector('.swatches__option-value');
      if (raw) insertAfter(span, raw); else head.appendChild(span);
    }
    renderWidthHeader();
  }

  function renderWidthHeader() {
    var c = widthContainer();
    if (!c) return;
    var raw = c.querySelector('.swatches__option-value');
    var out = c.querySelector('.pdp-r-width-label');
    if (!raw || !out) return;
    var word = widthLabelFor(raw.textContent);
    out.textContent = word ? ' (' + word + ')' : '';
  }

  /* --- Quantity beside the width chips -------------------------------- */
  function moveQuantity() {
    var qty = state.section.querySelector('.product__section--qty');
    if (!qty || qty.closest('.pdp-r-fit-row')) return;
    var swatches = state.section.querySelector('.product__variants-swatches');
    if (!swatches) return;
    var row = el('div', 'pdp-r-fit-row');
    var width = widthContainer();
    if (width && width.parentNode === swatches) {
      swatches.insertBefore(row, width);
      row.appendChild(width);
    } else {
      swatches.appendChild(row);
    }
    row.appendChild(qty);
  }

  /* --- Pickup availability into the delivery meta row ----------------- */
  function moveStoreAvailability() {
    var avail = state.section.querySelector('.product-single__store-availability-container');
    var row = state.section.querySelector('.product__form-buttons .atc-trust-row');
    if (!avail || !row || avail.parentNode === row) return;
    row.appendChild(avail);
    row.classList.add('pdp-r-meta');
  }

  /* --- Hide the star rating when the product has no reviews ----------- */
  /* Lipscore renders its compact widget client-side and, with zero reviews,
     shows five grey stars and "0.0 (0)" next to the price. Nothing in the
     theme suppresses that, so watch the widget and hide it once it resolves
     to a zero count. A non-zero count (or a widget that never renders) is
     left exactly as-is. */
  function hideEmptyRating() {
    var app = state.section.querySelector('.product__price-rating .product__section--app');
    var widget = state.section.querySelector('#lipscore-rating');
    if (!app || !widget) return;

    function count() {
      var txt = (widget.textContent || '').replace(/\s+/g, ' ');
      var m = txt.match(/\((\d+)\)/);
      return m ? parseInt(m[1], 10) : null;
    }

    /* A positive count is authoritative and final. A zero is only tentative:
       Lipscore can paint an empty widget first and fill the real count in a
       moment later, so keep watching until the settle timeout rather than
       locking the row hidden on the first reading. */
    function check() {
      var n = count();
      if (n === null) return false;          /* not rendered yet */
      if (n > 0) {
        app.classList.remove('pdp-r-hidden');
        return true;                          /* settled, stop watching */
      }
      app.classList.add('pdp-r-hidden');
      log('no reviews; rating widget hidden (provisional)');
      return false;
    }

    if (check()) return;
    if (!window.MutationObserver) return;
    var obs = new MutationObserver(function () { if (check()) obs.disconnect(); });
    obs.observe(widget, { childList: true, subtree: true, characterData: true });
    /* Lipscore absent or blocked: give up quietly, leaving whatever the last
       reading decided (unrendered = untouched, zero = hidden). */
    setTimeout(function () { obs.disconnect(); }, 15000);
  }

  /* --- Empty Key Features accordion (products with no description) ---- */
  function hideEmptyDescription() {
    var desc = state.section.querySelector('.product__section--desc');
    if (!desc) return;
    var inner = desc.querySelector('.c-accordion__inner');
    var body = inner || desc;
    var hasMedia = body.querySelector('img, video, iframe, table, ul, ol');
    if (!hasMedia && !body.textContent.trim()) {
      desc.classList.add('pdp-r-hidden');
      log('Key Features accordion is empty; hidden');
    }
  }

  /* --- Delivery & Returns accordion ----------------------------------- */
  function insertDeliveryAccordion() {
    if (state.section.querySelector('.pdp-r-delivery')) return;
    var tpl = document.querySelector('[data-pdp-r-delivery-template]');
    if (!tpl || !tpl.content || !tpl.content.firstElementChild) return;
    var node = tpl.content.firstElementChild.cloneNode(true);
    var content = state.section.querySelector('.product__section-content');
    if (!content) return;
    var about = state.section.querySelector('.about-brand');
    var anchor = about ? about.closest('.c-accordion--product') : null;
    if (!anchor) anchor = state.section.querySelector('.product__section--desc');
    if (anchor && anchor.parentNode) { insertAfter(node, anchor); return; }
    var reviews = state.section.querySelector('#lipscore-review-list');
    var reviewsWrap = reviews ? reviews.closest('.product__section--custom_liquid') : null;
    if (reviewsWrap) { reviewsWrap.parentNode.insertBefore(node, reviewsWrap); return; }
    var share = content.querySelector('.share-icons');
    if (share) content.insertBefore(node, share); else content.appendChild(node);
  }

  /* --- Gallery dots (mobile) ------------------------------------------ */
  function buildGalleryDots(attempt) {
    attempt = attempt || 0;
    var slider = state.section.querySelector('#slider');
    var main = state.section.querySelector('.js-carousel-main');
    if (!slider || !main || slider.querySelector('.pdp-r-dots')) return;
    var flkty = (window.Flickity && typeof window.Flickity.data === 'function') ? window.Flickity.data(main) : null;
    if (!flkty) {
      /* theme.Product may still be initialising; try again for a few seconds */
      if (attempt < 25) setTimeout(function () { safe(function () { buildGalleryDots(attempt + 1); }, 'gallery dots retry'); }, 200);
      return;
    }
    if (!flkty.cells || flkty.cells.length < 2) return;
    var dots = el('div', 'pdp-r-dots');
    dots.setAttribute('aria-hidden', 'true');
    flkty.cells.forEach(function (cell, i) {
      var b = el('button', 'pdp-r-dots__dot');
      b.type = 'button';
      b.tabIndex = -1;
      b.addEventListener('click', function () { flkty.select(i); });
      dots.appendChild(b);
    });
    slider.appendChild(dots);
    function paint() {
      var idx = flkty.selectedIndex;
      dots.querySelectorAll('.pdp-r-dots__dot').forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
      });
    }
    flkty.on('select', paint);
    paint();
  }

  /* --- ATC label mirrors availability -------------------------------- */
  function realAtc() {
    return state.section.querySelector('.product__form-buttons .js-ajax-submit');
  }

  function syncAtcLabel() {
    var btn = realAtc();
    if (!btn) return;
    /* Only overwrite plain labels; the ajax flow may briefly hold other text. */
    var text = btn.disabled ? state.formConfig.soldOut : state.formConfig.button;
    var current = btn.textContent.trim();
    if (current === state.formConfig.button || current === state.formConfig.soldOut || current === '') {
      btn.textContent = text;
    }
  }

  /* --- Swym wishlist button next to the ATC --------------------------- */
  function swymButton() {
    return state.section.querySelector('.swym-add-to-wishlist-view-product, .swym-button.swym-add-to-wishlist, [class*="swym-add-to-wishlist"]');
  }

  function placeSwym() {
    var swym = swymButton();
    var btn = realAtc();
    if (!swym || !btn) return;
    /* Swym may wrap its button; move the outermost swym-owned wrapper so its
       own structure stays intact. */
    var node = swym;
    while (node.parentNode && node.parentNode !== btn.parentNode && node.parentNode !== state.section &&
           node.parentNode.nodeType === 1 && /swym/i.test(node.parentNode.className || '')) {
      node = node.parentNode;
    }
    if (node.previousElementSibling !== btn || node.parentNode !== btn.parentNode) {
      insertAfter(node, btn);
      log('swym button placed after ATC');
    }
    node.classList.add('pdp-r-wish');
    updateSticky();
  }

  function watchSwym() {
    placeSwym();
    if (state.swymObserver || !window.MutationObserver) return;
    var timer;
    state.swymObserver = new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(placeSwym, 50);
    });
    state.swymObserver.observe(state.section, { childList: true, subtree: true });
    /* Swym only mounts after cookie consent; stop watching after a while. */
    setTimeout(function () {
      if (state.swymObserver) { state.swymObserver.disconnect(); state.swymObserver = null; }
    }, 60000);
  }

  /* --- Sticky bar (mobile) -------------------------------------------- */
  function buildStickyBar() {
    if (state.sticky || !state.section.querySelector('.product__section--buttons')) return;
    var bar = el('div', 'pdp-r-sticky');
    bar.setAttribute('data-pdp-r-sticky', '');
    bar.innerHTML =
      '<div class="pdp-r-sticky__info">' +
        '<div class="pdp-r-sticky__price"></div>' +
        '<div class="pdp-r-sticky__meta"></div>' +
      '</div>' +
      '<button type="button" class="pdp-r-sticky__atc"></button>' +
      '<button type="button" class="pdp-r-sticky__wish" aria-label="Add to wishlist" hidden>' +
        '<svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19.67 5.47c-1.99-1.64-4.95-1.35-6.78.48l-.89.89-.89-.89c-1.82-1.82-4.79-2.12-6.78-.48-2.28 1.88-2.4 5.27-.36 7.31l7.67 7.67c.2.2.51.2.71 0l7.67-7.67c2.05-2.04 1.93-5.42-.35-7.31z" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>';
    document.body.appendChild(bar);
    state.sticky = {
      bar: bar,
      price: bar.querySelector('.pdp-r-sticky__price'),
      meta: bar.querySelector('.pdp-r-sticky__meta'),
      atc: bar.querySelector('.pdp-r-sticky__atc'),
      wish: bar.querySelector('.pdp-r-sticky__wish')
    };

    state.sticky.atc.addEventListener('click', function () {
      var btn = realAtc();
      if (!btn || btn.disabled) return;
      capture('pdp_redesign_atc_click', { source: 'sticky' });
      state.proxyClick = true;
      btn.click();
      state.proxyClick = false;
    });
    state.sticky.wish.addEventListener('click', function () {
      var swym = swymButton();
      if (swym) swym.click();
    });
    var mainBtn = realAtc();
    if (mainBtn) {
      mainBtn.addEventListener('click', function () {
        if (state.proxyClick) return;
        capture('pdp_redesign_atc_click', { source: 'main' });
      });
    }

    /* Show once the buy box has scrolled up out of view. A scroll listener
       rather than IntersectionObserver: a fast fling (or an anchor jump) can
       move the buy box from below the viewport to above it between frames,
       and IO only reports intersection *changes*, so it would never fire. */
    var target = state.section.querySelector('.product__section--buttons');
    if (target) {
      /* one getBoundingClientRect per scroll event is cheap; no rAF so a
         throttled/background tab still updates as soon as it scrolls */
      var check = function () {
        var above = target.getBoundingClientRect().bottom < 0;
        if (root.classList.contains('pdp-r-sticky-visible') !== above) {
          root.classList.toggle('pdp-r-sticky-visible', above);
        }
      };
      window.addEventListener('scroll', check, { passive: true });
      window.addEventListener('resize', check, { passive: true });
      check();
    }
    updateSticky();
  }

  function updateSticky() {
    if (!state.sticky) return;
    var s = state.sticky;
    var price = state.section.querySelector('#product-price');
    if (price) {
      var onSale = price.classList.contains('price--on-sale');
      var sale = price.querySelector('.price__sale [data-sale-price]');
      var was = price.querySelector('.price__sale [data-regular-price]');
      var regular = price.querySelector('.price__regular [data-regular-price]');
      if (onSale && sale) {
        s.price.innerHTML = '<span class="pdp-r-sticky__now">' + sale.textContent.trim() + '</span>' +
          (was && was.textContent.trim() ? ' <span class="pdp-r-sticky__was">' + was.textContent.trim() + '</span>' : '');
      } else {
        s.price.textContent = (regular || sale) ? (regular || sale).textContent.trim() : '';
      }
    }
    var parts = [];
    var size = selectedSizeText();
    if (size) parts.push(size);
    var w = widthContainer();
    var wv = w && w.querySelector('.swatches__option-value');
    if (wv && wv.textContent.trim()) parts.push(wv.textContent.trim());
    var qty = state.section.querySelector('.js-qty-input');
    if (qty && qty.value) parts.push('Qty ' + qty.value);
    s.meta.textContent = parts.join(' · ');

    var btn = realAtc();
    if (btn) {
      s.atc.disabled = !!btn.disabled;
      s.atc.textContent = btn.disabled ? state.formConfig.soldOut : (btn.textContent.trim() || state.formConfig.button);
    }
    var swym = swymButton();
    s.wish.hidden = !swym;
    if (swym) s.wish.classList.toggle('is-added', swym.classList.contains('swym-added'));
  }

  /* --- Keep everything in step with the theme's variant events -------- */
  function bindUpdates() {
    /* theme.ProductForm dispatches a bubbling change on .formVariantId after
       price / availability / swatch state have been updated. */
    document.addEventListener('change', function (e) {
      if (!state.active) return;
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.classList && t.classList.contains('formVariantId')) {
        setTimeout(function () { syncAtcLabel(); updateSticky(); }, 0);
      }
      if (t.closest('.js-variant-selector')) {
        /* after the theme's own 50ms conversion hint rewrite */
        setTimeout(function () { renderSizeHeader(); renderWidthHeader(); updateSticky(); }, 80);
      }
      if (t.classList && t.classList.contains('js-qty-input')) updateSticky();
    });
    document.addEventListener('click', function (e) {
      if (!state.active) return;
      var t = e.target;
      if (t && t.closest && t.closest('.quantity-control-up, .quantity-control-down')) {
        setTimeout(updateSticky, 0);
      }
    });
  }

  /* --- Breakpoint-dependent placement --------------------------------- */
  function layout() {
    if (!state.active && !root.classList.contains(CLASS)) return;
    safe(placeTanya, 'tanya');
    updateSticky();
  }

  function placeTanya() {
    var tanya = state.section.querySelector('.tanyas-take');
    if (!tanya) return;
    var images = state.section.querySelector('.product__section-images');
    var details = state.section.querySelector('.product__section-details');
    if (!images || !details) return;
    if (MQ_DESKTOP.matches) {
      if (tanya.parentNode === images) return;
      images.appendChild(tanya);
      tanya.classList.add('pdp-r-tanya--gallery');
    } else {
      if (tanya.parentNode === details) return;
      /* Back into the details flow; CSS `order` positions it under the CTAs. */
      details.appendChild(tanya);
      tanya.classList.remove('pdp-r-tanya--gallery');
    }
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  var started = false;
  function start() {
    if (started) return;
    started = true;
    if (!document.querySelector('.product__section')) { log('not a product section page'); return; }
    /* The class is written server-side in layout/theme.liquid. If it is absent
       the redesign has been switched off there, so leave the page alone. */
    if (!root.classList.contains(CLASS)) { log('pdp-redesign class not present; standing down'); return; }
    enhance();
  }

  /* Manual re-run hook for debugging. */
  window.__applyPdpRedesign = function () { state.enhanced = false; enhance(); };

  /* This script is deferred, so it executes while readyState is already
     'interactive' but BEFORE DOMContentLoaded — and theme.js initialises
     theme.Product (Flickity, variant form, quantity) on DOMContentLoaded.
     Wait for that event so every enhancement finds an initialised page;
     `load` is the fallback if it has somehow already fired. */
  if (document.readyState === 'complete') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
    window.addEventListener('load', start);
  }
})();